// ─────────────────────────────────────────────────────────────────────────────
// ListPriceImportsComponent
// ─────────────────────────────────────────────────────────────────────────────
// The import journal: one row per uploaded price source, with its report.
//
// It exists because the extraction is deferred. The upload returns before any
// observation is created, so without this table an operator has no way to know
// whether their file was read, what it produced, or why it produced nothing. An
// accepted document that produces no observation must say so, with a machine
// reason — never a silent success and never a silent failure.
//
// Clicking a row opens the observations of that import; clicking the file name
// opens the file itself.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DATE_TIME_FORMAT, DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';

import { StatusBadgeTone } from 'src/app/ui/status-badge/status-badge.component';

import {
  IPriceImport,
  PRICE_IMPORT_PENDING_STATES,
  machineReasonLabel,
} from '../../models/price-observation.models';
// The same cadence as `awaitImports`, from the same place: two copies of "how
// often and for how long" would drift the first time one of them is tuned.
import { POLL_ATTEMPTS, POLL_INTERVAL_MS } from '../../services/price-observation.service';
import { PriceSourcePreviewService } from 'src/app/shared/file-preview-dialog/price-source-preview.service';
import { selectPriceImportItems, selectPriceImportMetadata } from '../../store/price-observations.selectors';
import { LoadPriceImports } from '../../store/price-observations.actions';

@Component({
  selector: 'app-list-price-imports',
  standalone: true,
  imports: [CommonModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-price-imports.component.html',
  styleUrls: ['./list-price-imports.component.scss'],
})
export class ListPriceImportsComponent implements OnInit, OnDestroy {
  @Output() openObservations = new EventEmitter<number>();

  imports$: Observable<IPriceImport[]> = this.store.select(selectPriceImportItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectPriceImportMetadata);

  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  rows: IPriceImport[] = [];

  /**
   * The extraction is deferred, so a row lands here `pending` and becomes
   * `done` seconds later, with the counts and the reasons this table exists to
   * show. Without re-asking, the operator would have to leave the tab and come
   * back to see them.
   *
   * Bounded on purpose: a stuck import stops the polling instead of asking for
   * ever, and the row keeps showing the state it is really in.
   */
  private pollAttemptsLeft = POLL_ATTEMPTS;
  private pollHandle?: Subscription;

  trackById(_index: number, row: IPriceImport): number {
    return row.id;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private sourcePreview: PriceSourcePreviewService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.imports$.pipe(takeUntil(this.destroy$)).subscribe((rows) => {
      this.rows = [...rows];
      this.schedulePoll();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.stopPoll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.store.dispatch(LoadPriceImports({ page: this.page }));
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    // A new page is a new question: the budget of the previous one does not
    // carry over.
    this.pollAttemptsLeft = POLL_ATTEMPTS;
    this.load();
  }

  // ── Deferred extraction ───────────────────────────────────────────────────

  /** Re-ask while a row of the page shown is still being read. */
  private schedulePoll(): void {
    this.stopPoll();
    if (!this.rows.some((row) => this.isRunning(row))) {
      this.pollAttemptsLeft = POLL_ATTEMPTS;
      return;
    }
    if (this.pollAttemptsLeft <= 0) return;
    this.pollAttemptsLeft -= 1;
    this.pollHandle = timer(POLL_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.load());
  }

  private stopPoll(): void {
    this.pollHandle?.unsubscribe();
    this.pollHandle = undefined;
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  /**
   * The audience chosen at upload, or "per message" for an email export.
   *
   * An email export cannot carry a scope — the model refuses one, because its
   * customers resolve message by message — so its empty column is an answer,
   * not a hole. Falling back to the source-kind label would echo the type
   * column one cell to the left and name an audience nobody chose.
   */
  scopeLabel(row: IPriceImport): string {
    if (!row.scope) {
      return row.source_kind === 'email_export'
        ? this.translateService.instant('knowledge_base.price_observations.scope.per_message')
        : '—';
    }
    const label = this.translateService.instant(
      `knowledge_base.price_observations.scope.${row.scope}`,
    );
    // Every customer the upload named, not just the first: an import published
    // to three accounts is three sets of rows, and naming one hides the others.
    const names = (row.customer_names ?? []).filter((name) => !!name);
    return row.scope === 'customer' && names.length
      ? `${label} — ${names.join(', ')}`
      : label;
  }

  /**
   * The tone of the state pill, for `<ui-status-badge>` — so the journal reads
   * from `$status-badge-palette` like every other list in the app instead of
   * painting its own hex.
   */
  stateTone(row: IPriceImport): StatusBadgeTone {
    switch (row.state) {
      case 'done':
        return 'success';
      case 'failed':
        return 'destructive';
      // Not a failure and not a finished import: there is a file left to read
      // and a question only the operator can answer.
      case 'awaiting_mapping':
        return 'alert';
      default:
        return 'draft';
    }
  }

  /** True while a file this import carried is waiting to be named. */
  needsMapping(row: IPriceImport): boolean {
    return row.state === 'awaiting_mapping';
  }

  /**
   * Ask the operator which column holds what, for the tariff this message
   * carried.
   *
   * Loaded on the click for the same reason the preview is: the mapping step
   * is only ever needed by the small share of imports that raise the question.
   */
  openMapping(row: IPriceImport): void {
    void import('../import-mapping-dialog/import-mapping-dialog.component').then(
      ({ ImportMappingDialogComponent }) => {
        this.dialog
          .open(ImportMappingDialogComponent, {
            data: { importId: row.id, filename: row.filename },
            width: '900px',
            maxWidth: '90vw',
            autoFocus: false,
          })
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.load());
      },
    );
  }

  /**
   * Open the file this import read. Price sources are not pushed to
   * knowledge-base storage, so the journal previews the file itself.
   * `document_id` is null on an archive: a zip is a download, not a preview.
   */
  openDocument(row: IPriceImport): void {
    if (!row.document_id) return;
    this.sourcePreview.open({ documentId: row.document_id, label: row.filename });
  }

  stateLabel(row: IPriceImport): string {
    return this.translateService.instant(`knowledge_base.price_observations.import_state.${row.state}`);
  }

  sourceKindLabel(row: IPriceImport): string {
    return this.translateService.instant(`knowledge_base.price_observations.source_kind.${row.source_kind}`);
  }

  /**
   * A reason that names work becomes the work, counted; everything else keeps
   * its factual label.
   *
   * The raw report is the machine's vocabulary — a diagnosis of the run, not a
   * thing to do. The four customer reasons describe one state the operator acts
   * on (`scope=unresolved`) and the backend treats them as one, so they say one
   * thing here too.
   *
   * The rest — a template mismatch, a locked archive, a row ceiling — are
   * events with nothing to count. `current_reasons()` server-side draws the
   * same line.
   */
  private static readonly ACTION_BY_REASON: Record<string, string> = {
    product_unresolved: 'product',
    no_customer_resolved: 'customer',
    email_ambiguous: 'customer',
    domain_ambiguous: 'customer',
    customer_resolution_below_threshold: 'customer',
    number_ambiguous: 'amount',
  };

  /**
   * The reasons, as what is left to do and what merely happened.
   *
   * Falls back to the raw key rather than hiding an unknown reason: a reason
   * the front has not translated yet still answers "why did this file produce
   * nothing", and swallowing it is the silent failure this journal prevents.
   */
  reasonLabels(row: IPriceImport): string[] {
    const entries = Object.entries(row.reasons ?? {});
    const labels: string[] = [];
    // One line per action, not per reason: three customer reasons on one file
    // are one queue to work through.
    const counts = new Map<string, number>();
    for (const [reason, count] of entries) {
      const action = ListPriceImportsComponent.ACTION_BY_REASON[reason];
      if (action) {
        counts.set(action, (counts.get(action) ?? 0) + count);
      } else {
        labels.push(this.factualLabel(reason, count));
      }
    }
    // Actions first: they are the only lines that ask for something.
    return [...[...counts].map(([action, count]) => this.actionLabel(action, count)), ...labels];
  }

  private actionLabel(action: string, count: number): string {
    const suffix = count > 1 ? 'many' : 'one';
    return this.translateService.instant(
      `knowledge_base.price_observations.reason_action.${action}_${suffix}`,
      { count },
    );
  }

  private factualLabel(reason: string, count: number): string {
    return machineReasonLabel(this.translateService, reason, count);
  }

  hasReasons(row: IPriceImport): boolean {
    return Object.keys(row.reasons ?? {}).length > 0;
  }

  /** A finished import that created nothing is the case worth pointing at. */
  producedNothing(row: IPriceImport): boolean {
    return row.state === 'done' && row.count_created === 0 && row.count_to_verify === 0;
  }

  isRunning(row: IPriceImport): boolean {
    return PRICE_IMPORT_PENDING_STATES.includes(row.state);
  }

  onRowClick(row: IPriceImport): void {
    this.openObservations.emit(row.id);
  }
}
